import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FiX, FiSearch } from 'react-icons/fi';
import PaymentConfirmationModal from './PaymentConfirmationModal';

const CreatePaymentForm = ({ onSuccess, onCancel }) => {
    const [formData, setFormData] = useState({
        paymentType: 'individual',
        godMember: null,
        godGroup: null,
        paymentAmount: '',
        paymentDate: new Date().toISOString().split('T')[0],
        renewalDate: '',
        paymentRemarks: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [members, setMembers] = useState([]);
    const [groups, setGroups] = useState([]);
    const [memberSearchTerm, setMemberSearchTerm] = useState('');
    const [groupSearchTerm, setGroupSearchTerm] = useState('');
    const [showMemberSearch, setShowMemberSearch] = useState(false);
    const [showGroupSearch, setShowGroupSearch] = useState(false);
    const [selectedMemberName, setSelectedMemberName] = useState('');
    const [selectedGroupName, setSelectedGroupName] = useState('');
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [pendingPaymentData, setPendingPaymentData] = useState(null);
    const [pendingGroupData, setPendingGroupData] = useState(null);

    useEffect(() => {
        if (memberSearchTerm) {
            fetchMembers();
        }
    }, [memberSearchTerm]);

    useEffect(() => {
        if (groupSearchTerm) {
            fetchGroups();
        }
    }, [groupSearchTerm]);

    const fetchMembers = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/godmembers/search?term=${encodeURIComponent(memberSearchTerm)}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true'
                    }
                }
            );
            if (response.data.success) {
                setMembers(response.data.data);
            }
        } catch (err) {
            console.error('Error fetching members:', err);
        }
    };

    const fetchGroups = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/godgroups?search=${groupSearchTerm}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true'
                    }
                }
            );
            if (response.data.success) {
                setGroups(response.data.data);
            }
        } catch (err) {
            console.error('Error fetching groups:', err);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        // Validate required fields
        if (!formData.godMember && !formData.godGroup) {
            setError('Please select a member or group');
            return;
        }

        if (!formData.paymentAmount || !formData.paymentDate) {
            setError('Please fill in all required fields');
            return;
        }

        if (formData.paymentType !== 'misc_fees' && !formData.renewalDate) {
            setError('Please fill in all required fields');
            return;
        }

        // Show confirmation modal instead of directly creating payment
        let groupForConfirm = selectedGroup;
        if (formData.paymentType === 'group' || formData.paymentType === 'add_on') {
            try {
                const token = localStorage.getItem('token');
                const response = await axios.get(
                    `${process.env.REACT_APP_API_URL}/api/godgroups/${formData.godGroup}`,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'ngrok-skip-browser-warning': 'true',
                        },
                    }
                );
                if (response.data.success) {
                    groupForConfirm = response.data.data;
                    setSelectedGroup(groupForConfirm);
                }
            } catch (err) {
                console.error('Error fetching group for payment confirm:', err);
            }
        }
        setPendingPaymentData(formData);
        setPendingGroupData(groupForConfirm || selectedGroup || null);
        setShowConfirmation(true);
    };

    const handleConfirmPayment = async () => {
        setLoading(true);
        setError('');

        try {
            const token = localStorage.getItem('token');
            const payload = { ...pendingPaymentData };
            if (payload.paymentType === 'misc_fees') {
                payload.renewalDate = null;
                payload.godGroup = null;
            }
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/api/payments`,
                payload,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true'
                    }
                }
            );

            if (response.data.success) {
                setShowConfirmation(false);
                setPendingPaymentData(null);
                setPendingGroupData(null);
                onSuccess(response.data.data);
            }
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleCancelConfirmation = () => {
        setShowConfirmation(false);
        setPendingPaymentData(null);
        setPendingGroupData(null);
        // Keep form data intact for editing
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'paymentType') {
            setFormData(prev => ({
                ...prev,
                paymentType: value,
                godMember: null,
                godGroup: null,
                renewalDate: value === 'misc_fees' ? '' : prev.renewalDate
            }));
            setSelectedMemberName('');
            setSelectedGroupName('');
            setSelectedGroup(null);
            return;
        }
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const selectMember = (member) => {
        setFormData(prev => ({
            ...prev,
            godMember: member._id
        }));
        setSelectedMemberName(member.fullName);
        setShowMemberSearch(false);
        setMemberSearchTerm('');
    };

    const selectGroup = (group) => {
        setFormData(prev => ({
            ...prev,
            godGroup: group._id
        }));
        setSelectedGroupName(group.groupName);
        setSelectedGroup(group);
        setShowGroupSearch(false);
        setGroupSearchTerm('');
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString();
    };

    // Get member/group data for confirmation modal
    const getMemberData = () => {
        if ((formData.paymentType === 'individual' || formData.paymentType === 'misc_fees') && formData.godMember) {
            return members.find(m => m._id === formData.godMember);
        }
        return null;
    };

    const getGroupData = () => {
        if ((formData.paymentType === 'group' || formData.paymentType === 'add_on') && formData.godGroup) {
            if (selectedGroup && String(selectedGroup._id) === String(formData.godGroup)) return selectedGroup;
            return groups.find(g => String(g._id) === String(formData.godGroup));
        }
        return null;
    };

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full">
            <div className="relative top-20 mx-auto p-5 border w-[600px] shadow-lg rounded-md bg-white">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium">Create New Payment</h3>
                    <button onClick={onCancel} className="text-gray-500 hover:text-gray-700">
                        <FiX />
                    </button>
                </div>

                {error && (
                    <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md">
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Payment Type</label>
                        <select
                            name="paymentType"
                            value={formData.paymentType}
                            onChange={handleChange}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            required
                        >
                            <option value="individual">Individual</option>
                            <option value="group">Group</option>
                            <option value="add_on">Add-on</option>
                            <option value="misc_fees">Misc Fees</option>
                        </select>
                    </div>

                    {(formData.paymentType === 'individual' || formData.paymentType === 'misc_fees') && (
                        <div className="relative">
                            <label className="block text-sm font-medium text-gray-700">Member</label>
                            <div className="mt-1 flex rounded-md shadow-sm">
                                <div className="relative flex-grow focus-within:z-10">
                                    <input
                                        type="text"
                                        value={selectedMemberName || memberSearchTerm}
                                        onChange={(e) => {
                                            setMemberSearchTerm(e.target.value);
                                            setSelectedMemberName('');
                                            setFormData(prev => ({ ...prev, godMember: null }));
                                        }}
                                        onFocus={() => {
                                            setShowMemberSearch(true);
                                            if (selectedMemberName) {
                                                setMemberSearchTerm('');
                                                setSelectedMemberName('');
                                            }
                                        }}
                                        placeholder="Search member..."
                                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                    />
                                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                                        <FiSearch className="text-gray-400" />
                                    </div>
                                </div>
                            </div>
                            {showMemberSearch && members.length > 0 && (
                                <div className="absolute z-10 w-full mt-1 bg-white rounded-md shadow-lg">
                                    <ul className="max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                                        {members.map((member) => (
                                            <li
                                                key={member._id}
                                                onClick={() => selectMember(member)}
                                                className="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-50"
                                            >
                                                <div className="flex items-center">
                                                    <span className="font-normal block truncate">
                                                        {member.fullName}
                                                    </span>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            {!showMemberSearch && selectedMemberName && (
                                <div className="mt-1 text-sm text-gray-500">
                                    Selected: {selectedMemberName}
                                </div>
                            )}
                        </div>
                    )}

                    {(formData.paymentType === 'group' || formData.paymentType === 'add_on') && (
                        <div className="relative">
                            <label className="block text-sm font-medium text-gray-700">Group</label>
                            <div className="mt-1 flex rounded-md shadow-sm">
                                <div className="relative flex-grow focus-within:z-10">
                                    <input
                                        type="text"
                                        value={selectedGroupName || groupSearchTerm}
                                        onChange={(e) => {
                                            setGroupSearchTerm(e.target.value);
                                            setSelectedGroupName('');
                                            setFormData(prev => ({ ...prev, godGroup: null }));
                                        }}
                                        onFocus={() => {
                                            setShowGroupSearch(true);
                                            if (selectedGroupName) {
                                                setGroupSearchTerm('');
                                                setSelectedGroupName('');
                                            }
                                        }}
                                        placeholder="Search group..."
                                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                    />
                                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                                        <FiSearch className="text-gray-400" />
                                    </div>
                                </div>
                            </div>
                            {showGroupSearch && groups.length > 0 && (
                                <div className="absolute z-10 w-full mt-1 bg-white rounded-md shadow-lg">
                                    <ul className="max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                                        {groups.map((group) => (
                                            <li
                                                key={group._id}
                                                onClick={() => selectGroup(group)}
                                                className="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-50"
                                            >
                                                <div className="flex items-center">
                                                    <span className="font-normal block truncate">
                                                        {group.groupName}
                                                    </span>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            {!showGroupSearch && selectedGroupName && (
                                <>
                                    <div className="mt-1 text-sm text-gray-500">
                                        <span>Selected: {selectedGroupName}</span>
                                        {groups.find(g => g._id === formData.godGroup)?.renewalDate && (
                                            <span className="ml-2">
                                                - Renewal Date: <span className="text-red-600">
                                                    {new Date(groups.find(g => g._id === formData.godGroup).renewalDate).toLocaleDateString()}
                                                </span>
                                            </span>
                                        )}
                                    </div>
                                    {/* Show active participants */}
                                    {groups.find(g => g._id === formData.godGroup)?.groupMembers?.participants
                                        .filter(p => !p.removeDate)
                                        .map((participant, index) => (
                                            <div key={index} className="mt-2 p-2 bg-gray-50 rounded-md">
                                                <div className="text-sm">
                                                    <span className="font-medium">{participant.memberID?.fullName}</span>
                                                    <span className="text-gray-500 ml-2">
                                                        (Joined: {formatDate(participant.joinDate)})
                                                    </span>
                                                </div>
                                            </div>
                                        ))
                                    }
                                </>
                            )}
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium text-gray-700">Amount</label>
                        <div className="mt-1 relative rounded-md shadow-sm">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <span className="text-gray-500 sm:text-sm">$</span>
                            </div>
                            <input
                                type="number"
                                name="paymentAmount"
                                value={formData.paymentAmount}
                                onChange={handleChange}
                                className="block w-full pl-7 pr-12 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                placeholder="0.00"
                                required
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700">Payment Date</label>
                        <input
                            type="date"
                            name="paymentDate"
                            value={formData.paymentDate}
                            onChange={handleChange}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            required
                        />
                    </div>

                    {formData.paymentType !== 'misc_fees' && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Renewal Date</label>
                        <input
                            type="date"
                            name="renewalDate"
                            value={formData.renewalDate}
                            onChange={handleChange}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            required
                        />
                    </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium text-gray-700">Remarks</label>
                        <textarea
                            name="paymentRemarks"
                            value={formData.paymentRemarks}
                            onChange={handleChange}
                            rows="3"
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            placeholder="Add any remarks here..."
                        />
                    </div>

                    <div className="flex justify-end space-x-2">
                        <button
                            type="button"
                            onClick={onCancel}
                            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300"
                        >
                            {loading ? 'Creating...' : 'Create Payment'}
                        </button>
                    </div>
                </form>

                {/* Payment Confirmation Modal */}
                {showConfirmation && pendingPaymentData && (
                    <PaymentConfirmationModal
                        isOpen={showConfirmation}
                        onConfirm={handleConfirmPayment}
                        onCancel={handleCancelConfirmation}
                        paymentData={pendingPaymentData}
                        memberData={getMemberData()}
                        groupData={pendingGroupData || getGroupData()}
                    />
                )}
            </div>
        </div>
    );
};

export default CreatePaymentForm; 